import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Send,
  Printer,
  ArrowLeft,
  Loader2,
  Volume2,
  VolumeX,
  Mic,
  Square,
  Settings,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import ProviderSearch from "@/components/ProviderSearch";
import AsherFilterCard from "@/components/AsherFilterCard";
import PHIDisclaimerBanner from "@/components/PHIDisclaimerBanner";
import AsherVoiceOnboardingModal from "@/components/AsherVoiceOnboardingModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Preferred male voices, gentlest/youngest-sounding first. Microsoft "Natural"
// / "Online" voices are cloud-neural — dramatically warmer and younger-sounding
// than the legacy Aaron/Alex/Fred system voices, so they always come first.
// Order chosen for a "gentle, welcoming, ~30-year-old male" feel.
const MALE_VOICE_PREFERENCES = [
  // Microsoft neural (Windows Edge / Chromium on Windows) — cloud-quality
  "Andrew",           // en-US, warm mid-30s male — best "gentle welcoming"
  "Brian",            // en-US, warm friendly male
  "Christopher",      // en-US, natural young male
  "Guy",              // en-US, mid-thirties, conversational
  "Ryan",             // en-US, young natural male
  "Eric",             // en-US, warm young male
  "Roger",            // en-US, casual young male
  "Steffan",          // en-US, natural male
  // Apple newer (macOS 14+ / iOS 17+) enhanced/premium voices
  "Reed",             // premium enhanced male, warm
  "Rocko",            // premium enhanced male, friendly
  // Google Chrome
  "Google US English",
  // Apple legacy — LAST because they sound the most dated
  "Aaron",
  "Alex",
];

// Words in a voice name that are strong signals this is a NEW neural voice.
const NEURAL_HINTS = ["natural", "online", "neural", "enhanced", "premium"];

// Return only US-English voices, in preference order, with the gentlest
// male neural voices first, then legacy male voices, then any US voice.
const listUSMaleVoices = () => {
  if (typeof window === "undefined" || !window.speechSynthesis) return [];
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return [];
  // Any en-US flavor. Some neural voices report "en-US" while others fall
  // back to a generic "en" — accept both so we don't hide new voices.
  const usVoices = voices.filter(v => {
    const lang = (v.lang || "").toLowerCase();
    return lang === "en-us" || lang.startsWith("en-us") || lang === "en";
  });
  const isFemaleName = (n = "") => /female|zira|jenny|aria|ava|emma|linda|susan|allison|samantha|serena|karen|moira|tessa|kate|nicky|siri.*female/i.test(n);

  const ordered = [];
  const seen = new Set();
  // 1) Preferred neural/warm male voices in order. Match on name substring
  //    so "Microsoft Andrew Online (Natural) - English (United States)" hits
  //    on "Andrew".
  for (const name of MALE_VOICE_PREFERENCES) {
    const match = usVoices.find(
      v => !seen.has(v.voiceURI)
        && v.name.toLowerCase().includes(name.toLowerCase())
        && !isFemaleName(v.name)
    );
    if (match) { ordered.push(match); seen.add(match.voiceURI); }
  }
  // 2) Any en-US voice whose name contains a neural hint AND isn't clearly female.
  for (const v of usVoices) {
    if (seen.has(v.voiceURI)) continue;
    const nm = v.name.toLowerCase();
    if (NEURAL_HINTS.some(h => nm.includes(h)) && !isFemaleName(v.name)) {
      ordered.push(v); seen.add(v.voiceURI);
    }
  }
  // 3) Any US voice explicitly tagged "male" (and not female).
  for (const v of usVoices) {
    if (seen.has(v.voiceURI)) continue;
    if (/\bmale\b/i.test(v.name) && !isFemaleName(v.name)) {
      ordered.push(v); seen.add(v.voiceURI);
    }
  }
  // 4) Fill with remaining en-US voices that don't look female (last resort
  //    so the picker isn't empty on systems with only default voices).
  for (const v of usVoices) {
    if (!seen.has(v.voiceURI) && !isFemaleName(v.name)) {
      ordered.push(v); seen.add(v.voiceURI);
    }
  }
  return ordered;
};

const pickDefaultVoice = (savedURI) => {
  const voices = listUSMaleVoices();
  if (!voices.length) return null;
  if (savedURI) {
    const saved = voices.find(v => v.voiceURI === savedURI);
    if (saved) return saved;
  }
  return voices[0];
};

// Strip markdown-ish characters that shouldn't be spoken
const cleanForSpeech = (text = "") =>
  text
    .replace(/[*_`#>]/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")   // markdown links -> label only
    .replace(/https?:\/\/\S+/g, "")        // strip raw URLs
    .replace(/\s+/g, " ")
    .trim();

export default function ChatPage() {
  const { sessionId: paramSessionId } = useParams();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(paramSessionId || `session_${Date.now()}`);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showProviderSearch, setShowProviderSearch] = useState(false);
  const [careTypeRecommendation, setCareTypeRecommendation] = useState(null);
  const [recommendedCareTypes, setRecommendedCareTypes] = useState([]);
  const [autoSearchLocation, setAutoSearchLocation] = useState(null);
  // Narrow-down filters (Feb 2026): the AsherFilterCard is a self-contained
  // 3-stage component (prompt → expanded → collapsed link) that stays mounted
  // once Asher has a care direction. We only need to hold the filter payload
  // it produces so ProviderSearch picks it up.
  const [narrowFilters, setNarrowFilters] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const providerSearchRef = useRef(null);

  // ---- Voice (TTS) ----
  const supportsSpeech = typeof window !== "undefined" && "speechSynthesis" in window;
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem("asher_voice_enabled");
    return stored === null ? true : stored === "true";
  });
  const [savedVoiceURI, setSavedVoiceURI] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem("asher_voice_uri") || null;
  });
  const [availableVoices, setAvailableVoices] = useState([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const voiceRef = useRef(null);
  const hasSpokenRef = useRef(false); // gates the initial greeting until user gesture
  // ElevenLabs — server-allowlisted premium voices. When available we use these
  // over the browser's Web Speech API because the neural voices sound identical
  // across every device (Mac, Windows, iOS, Android).
  const [elevenVoices, setElevenVoices] = useState([]); // [{voice_id, name, description}]
  const [elevenDefaultId, setElevenDefaultId] = useState(null);
  const audioRef = useRef(null);
  const audioObjectUrlRef = useRef(null);

  // Fetch the ElevenLabs voice list once. If it fails (unauth, offline) we
  // silently fall back to Web Speech.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/asher/voices`);
        if (!alive) return;
        setElevenVoices(data.voices || []);
        setElevenDefaultId(data.default_voice_id || null);
      } catch (e) {
        // no-op — Web Speech remains
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load Web Speech voices as a fallback (some browsers populate them async).
  useEffect(() => {
    if (!supportsSpeech) return;
    const loadVoices = () => {
      const voices = listUSMaleVoices();
      setAvailableVoices(voices);
      voiceRef.current = pickDefaultVoice(savedVoiceURI);
    };
    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
      window.speechSynthesis.cancel();
    };
  }, [supportsSpeech, savedVoiceURI]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* no-op */ }
      audioRef.current = null;
    }
    if (audioObjectUrlRef.current) {
      URL.revokeObjectURL(audioObjectUrlRef.current);
      audioObjectUrlRef.current = null;
    }
    if (supportsSpeech) {
      try { window.speechSynthesis.cancel(); } catch (_) { /* no-op */ }
    }
    setIsSpeaking(false);
  }, [supportsSpeech]);

  // Fallback path removed intentionally — we never speak with the browser
  // Web Speech voices (they sound harsh/robotic and the user said they were
  // "terrifying"). If ElevenLabs isn't ready or the play() call is blocked
  // by autoplay policy, we stay silent until the user's next interaction.
  //
  // Reliability additions (Feb 2026):
  // - Pending-utterance queue: if voices haven't loaded yet we hold the text
  //   and speak it as soon as `elevenVoices` arrives (prevents the first
  //   greeting or reply from silently dropping on slow connections).
  // - Single automatic retry on transient /api/asher/speak failures.
  // - Autoplay-blocked detection: when Chrome/Safari revokes the user-gesture
  //   window (usually because the LLM took >5s), we surface a one-tap toast
  //   instead of silently going mute — the user taps once and audio resumes.
  const pendingSpeakRef = useRef(null);      // { text, voiceId } — queued while voices load
  const quotaNoticeShownRef = useRef(false); // one-time notice when ElevenLabs credits run out
  const blockedUtteranceRef = useRef(null);  // last utterance the browser refused to autoplay — retried on the next user gesture

  const fetchAndPlay = useCallback(async (cleaned, voiceId) => {
    const resp = await axios.post(
      `${API}/asher/speak`,
      { text: cleaned, voice_id: voiceId },
      { responseType: "blob" }
    );
    const blob = resp.data;
    if (!blob || !blob.type || !blob.type.startsWith("audio/")) {
      throw new Error("Invalid audio response");
    }
    const objectUrl = URL.createObjectURL(blob);
    audioObjectUrlRef.current = objectUrl;
    const audio = new Audio(objectUrl);
    audioRef.current = audio;
    audio.onended = () => { setIsSpeaking(false); };
    audio.onerror = () => { setIsSpeaking(false); };
    await audio.play();
  }, []);

  const speak = useCallback(async (text, voiceIdOverride) => {
    if (!voiceEnabled) return;
    const cleaned = cleanForSpeech(text);
    if (!cleaned) return;
    stopAudio();
    // ElevenLabs voice list still loading? Queue this utterance and speak it
    // as soon as voices arrive — dropping it silently was the #1 reason
    // families said "Asher stopped talking" on production.
    if (!elevenVoices.length) {
      pendingSpeakRef.current = { text, voiceId: voiceIdOverride };
      return;
    }
    setIsSpeaking(true);
    hasSpokenRef.current = true;
    const voiceId = voiceIdOverride || savedVoiceURI || elevenDefaultId;
    try {
      await fetchAndPlay(cleaned, voiceId);
    } catch (e) {
      // Quota-exceeded (HTTP 402) — voice credits ran out. Tell the user
      // once so they aren't confused when Asher goes text-only, then flip
      // voice off for this session so we don't hammer a broken service.
      const status = e?.response?.status;
      if (status === 402) {
        if (!quotaNoticeShownRef.current) {
          quotaNoticeShownRef.current = true;
          toast("Asher's voice is temporarily paused — text replies still work fine.", {
            duration: 10000,
          });
        }
        setIsSpeaking(false);
        return;
      }
      const isAutoplay = e && (e.name === "NotAllowedError" || /interact|gesture|user activation|play\(\)/i.test(String(e.message || "")));
      if (isAutoplay) {
        // Chrome/Safari revoked the autoplay window (LLM took >5s to reply,
        // family switched tabs, etc). We don't interrupt the chat with a
        // popup — the family already chose "Speak to me aloud" at the start,
        // no need to re-ask them mid-conversation. Instead, stash the
        // utterance and the page-wide gesture listener will silently replay
        // it the moment the family clicks, taps, or types anything.
        blockedUtteranceRef.current = { cleaned, voiceId };
        setIsSpeaking(false);
        return;
      }
      // One silent retry for transient network / 5xx blips before giving up.
      try {
        await new Promise((r) => setTimeout(r, 400));
        await fetchAndPlay(cleaned, voiceId);
      } catch (retryErr) {
        // If the retry surfaced a quota error, still show the friendly notice.
        if (retryErr?.response?.status === 402 && !quotaNoticeShownRef.current) {
          quotaNoticeShownRef.current = true;
          toast("Asher's voice is temporarily paused — text replies still work fine.", {
            duration: 10000,
          });
        } else {
          console.warn("[Asher TTS] failed after retry:", retryErr);
        }
        setIsSpeaking(false);
      }
    }
  }, [voiceEnabled, elevenVoices.length, elevenDefaultId, savedVoiceURI, stopAudio, fetchAndPlay]);

  // Flush any queued utterance the moment ElevenLabs voices finish loading —
  // this fixes the case where the greeting/first reply arrived before the
  // voice list did and got dropped.
  useEffect(() => {
    if (!elevenVoices.length) return;
    const pending = pendingSpeakRef.current;
    if (!pending) return;
    pendingSpeakRef.current = null;
    speak(pending.text, pending.voiceId);
  }, [elevenVoices.length, speak]);

  const toggleVoice = () => {
    const next = !voiceEnabled;
    setVoiceEnabled(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("asher_voice_enabled", String(next));
    }
    if (!next) {
      stopAudio();
    }
  };

  const chooseVoice = async (voiceId) => {
    setSavedVoiceURI(voiceId);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("asher_voice_uri", voiceId);
    }
    // Preview using the freshly-chosen voice id (avoids stale-closure).
    if (voiceEnabled) {
      speak(
        "Hi, I'm Asher — your care guide. Take a breath. Whatever you're navigating, we'll figure it out together, one step at a time.",
        voiceId
      );
    }
  };


  // ---- Voice input (Mic → Whisper) ----
  const supportsMic = typeof window !== "undefined"
    && typeof navigator !== "undefined"
    && !!navigator.mediaDevices?.getUserMedia
    && typeof window.MediaRecorder !== "undefined";
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  const stopMicStream = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!supportsMic || isRecording || isTranscribing || isLoading) return;
    // Stop any current TTS so Asher's voice doesn't get transcribed
    stopAudio();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      // Pick a mimeType the browser supports — webm/opus is universal on Chrome/Edge/Android
      let mimeType = "audio/webm;codecs=opus";
      if (!window.MediaRecorder.isTypeSupported(mimeType)) mimeType = "audio/webm";
      if (!window.MediaRecorder.isTypeSupported(mimeType)) mimeType = "audio/mp4"; // Safari
      const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stopMicStream();
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        audioChunksRef.current = [];
        if (blob.size < 500) {
          toast.error("That was too short — try holding for a full sentence.");
          return;
        }
        await transcribeAndSend(blob);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Mic error:", err);
      stopMicStream();
      setIsRecording(false);
      if (err?.name === "NotAllowedError") {
        toast.error("Microphone permission denied. Please allow mic access in your browser.");
      } else {
        toast.error("Couldn't start the microphone. Please try again.");
      }
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try { recorder.stop(); } catch (e) { /* ignore */ }
    }
    setIsRecording(false);
  };

  const toggleRecording = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const transcribeAndSend = async (blob) => {
    setIsTranscribing(true);
    try {
      const ext = blob.type.includes("mp4") ? "m4a" : "webm";
      const form = new FormData();
      form.append("audio", blob, `voice.${ext}`);
      const resp = await axios.post(`${API}/chat/transcribe`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const text = (resp.data?.text || "").trim();
      if (!text) {
        toast.error("Couldn't hear that clearly. Please try again.");
        return;
      }
      // Auto-send the transcribed message so it feels like a real conversation
      await sendMessageText(text);
    } catch (err) {
      console.error("Transcribe error:", err);
      toast.error("Transcription failed. Please try again or type your message.");
    } finally {
      setIsTranscribing(false);
    }
  };

  // Stop mic & TTS on unmount
  useEffect(() => {
    return () => {
      stopMicStream();
      stopAudio();
    };
  }, [stopAudio]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversation = useCallback(async (sid) => {
    try {
      const response = await axios.get(`${API}/chat/conversation/${sid}`);
      setMessages(response.data.messages);
      if (response.data.care_type_recommendation) {
        setCareTypeRecommendation(response.data.care_type_recommendation);
      }
      if (Array.isArray(response.data.recommended_care_types) && response.data.recommended_care_types.length) {
        setRecommendedCareTypes(response.data.recommended_care_types);
        setShowProviderSearch(true);
      }
    } catch (error) {
      console.error("Error loading conversation:", error);
      toast.error("Failed to load conversation");
    }
  }, []);

  useEffect(() => {
    if (paramSessionId) {
      loadConversation(paramSessionId);
    } else {
      // Send initial greeting from Asher
      const greeting = {
        role: "assistant",
        content: "Hi there! I'm Asher, your care guide. What's your first name?",
        timestamp: new Date().toISOString()
      };
      setMessages([greeting]);
    }
  }, [paramSessionId, loadConversation]);

  // Speak the greeting once — but ONLY after (a) ElevenLabs voices have
  // loaded and (b) the user has interacted with the page at least once
  // (browsers block Audio.play() before a user gesture). We set
  // `userHasInteractedRef` in a page-wide pointerdown listener.
  const greetingSpokenRef = useRef(false);
  const userHasInteractedRef = useRef(false);
  useEffect(() => {
    const markInteracted = () => { userHasInteractedRef.current = true; };
    window.addEventListener("pointerdown", markInteracted, { once: true });
    window.addEventListener("keydown", markInteracted, { once: true });
    return () => {
      window.removeEventListener("pointerdown", markInteracted);
      window.removeEventListener("keydown", markInteracted);
    };
  }, []);

  // Global "retry any blocked utterance on the next user gesture" listener.
  // If autoplay blocked one of Asher's replies, we stashed the utterance in
  // blockedUtteranceRef. The moment the family clicks or types anywhere on
  // the page (fresh user activation), we replay it silently — no popup
  // interrupts the chat because the family already opted into voice at the
  // session's start. Runs for the life of the page.
  useEffect(() => {
    const retryBlocked = async () => {
      const pending = blockedUtteranceRef.current;
      if (!pending) return;
      // Only retry once per block — clear ref before the async call so
      // rapid gestures don't double-fire.
      blockedUtteranceRef.current = null;
      try {
        await fetchAndPlay(pending.cleaned, pending.voiceId);
      } catch (err) {
        console.warn("[Asher TTS] silent retry-on-gesture failed:", err);
      }
    };
    window.addEventListener("pointerdown", retryBlocked);
    window.addEventListener("keydown", retryBlocked);
    return () => {
      window.removeEventListener("pointerdown", retryBlocked);
      window.removeEventListener("keydown", retryBlocked);
    };
  }, [fetchAndPlay]);
  useEffect(() => {
    if (greetingSpokenRef.current) return;
    if (paramSessionId) return;
    if (!messages.length) return;
    if (!elevenVoices.length) return; // wait for ElevenLabs to be ready
    const first = messages[0];
    if (first.role !== "assistant") return;
    // Poll until the user has interacted, then speak the greeting.
    const interval = setInterval(() => {
      if (userHasInteractedRef.current && !greetingSpokenRef.current) {
        greetingSpokenRef.current = true;
        clearInterval(interval);
        speak(first.content);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [messages, paramSessionId, speak, elevenVoices.length]);

  const sendMessageText = async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || isLoading) return;

    const userMessage = {
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      // 25-second timeout — long enough for a slow LLM reply, short enough
      // that a hung request surfaces a clear error instead of an eternal spinner.
      const response = await axios.post(
        `${API}/chat/message`,
        { session_id: sessionId, message: text },
        { timeout: 25000 }
      );

      const asherMessage = {
        role: "assistant",
        content: response.data.message,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, asherMessage]);
      // Auto-speak Asher's reply — the user just tapped Send, so audio is unlocked
      speak(response.data.message);

      if (response.data.care_type_suggested) {
        setCareTypeRecommendation(response.data.care_type_suggested);
      }
      if (Array.isArray(response.data.recommended_care_types)) {
        setRecommendedCareTypes(response.data.recommended_care_types);
      }

      if (response.data.ready_for_providers) {
        setShowProviderSearch(true);
      }

      // If Asher detected a zip/city in the user's message, auto-seed the provider search
      if (response.data.location_extracted) {
        setShowProviderSearch(true);
        setAutoSearchLocation({
          value: response.data.location_extracted,
          timestamp: Date.now(),  // Forces re-trigger if user sends same location twice
        });
      }

      // Smooth-scroll to the provider search once it's visible
      if (response.data.ready_for_providers || response.data.location_extracted) {
        setTimeout(() => {
          providerSearchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Distinguish timeout / network from generic failure so the family
      // gets a helpful, human message instead of a mysterious "failed."
      const isTimeout = error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "");
      const isNetwork = !error?.response && !isTimeout;
      if (isTimeout) {
        toast.error("Asher is taking longer than usual. Please tap Send again — we'll try once more.", { duration: 6000 });
      } else if (isNetwork) {
        toast.error("Connection hiccup. Check your internet and tap Send again.", { duration: 6000 });
      } else {
        toast.error("Something went wrong sending that message. Please try again.");
      }
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSendMessage = () => sendMessageText(inputMessage);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePrint = () => {
    navigate(`/print/${sessionId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <AsherVoiceOnboardingModal
        onChoose={(enabled) => {
          setVoiceEnabled(enabled);
          // The click that dismissed the modal IS the user gesture browsers
          // need before Audio.play() will work — mark it now so the greeting
          // fires immediately for people who picked "Speak aloud".
          userHasInteractedRef.current = true;
        }}
      />
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-button"
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-primary hover:bg-primary/5"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className={`relative ${isSpeaking ? "asher-avatar-speaking" : ""}`}>
                  {/* Glowing beam ring — only visible when Asher is speaking */}
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute inset-0 rounded-full transition-opacity duration-500 ${
                      isSpeaking ? "opacity-100" : "opacity-0"
                    }`}
                    style={{
                      boxShadow: "0 0 0 3px rgba(42,173,160,0.35), 0 0 22px 6px rgba(42,173,160,0.55), 0 0 44px 10px rgba(224,123,84,0.35)",
                      animation: isSpeaking ? "asher-beam-pulse 1.6s ease-in-out infinite" : "none",
                    }}
                  />
                  <img 
                    src="/asher-avatar.png?v=2" 
                    alt="Asher" 
                    data-testid="asher-avatar"
                    onError={(e) => { e.currentTarget.src = "/asher-avatar-256.png"; }}
                    className={`relative w-11 h-11 rounded-full object-cover shadow-md ring-2 ring-primary/20 ${
                      isSpeaking ? "asher-avatar-float" : ""
                    }`}
                  />
                </div>
                <div>
                  <h1 className="text-xl font-heading font-bold text-primary">Asher</h1>
                  <p className="text-xs text-text-secondary font-body">
                    {isSpeaking ? "Speaking…" : "Your Care Guide"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {elevenVoices.length > 0 && (
                <Button
                  data-testid="voice-toggle-button"
                  onClick={toggleVoice}
                  variant="ghost"
                  size="sm"
                  title={voiceEnabled ? "Mute Asher's voice" : "Turn on Asher's voice"}
                  aria-label={voiceEnabled ? "Mute Asher's voice" : "Turn on Asher's voice"}
                  className={`rounded-full h-10 w-10 p-0 transition-colors ${
                    voiceEnabled ? "text-primary hover:bg-primary/10" : "text-text-secondary hover:bg-slate-100"
                  }`}
                >
                  {voiceEnabled ? (
                    <Volume2 className={`w-5 h-5 ${isSpeaking ? "animate-pulse" : ""}`} />
                  ) : (
                    <VolumeX className="w-5 h-5" />
                  )}
                </Button>
              )}
              {elevenVoices.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="voice-settings-button"
                      variant="ghost"
                      size="sm"
                      title="Choose Asher's voice"
                      aria-label="Choose Asher's voice"
                      className="rounded-full h-10 w-10 p-0 text-text-secondary hover:bg-slate-100 hover:text-primary"
                    >
                      <Settings className="w-5 h-5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="w-80 p-0 rounded-xl border border-[#E2E8F0] shadow-xl"
                    data-testid="voice-picker-popover"
                  >
                    <div className="px-4 pt-4 pb-2">
                      <p className="font-heading font-bold text-primary text-base">
                        Asher&apos;s Voice
                      </p>
                      <p className="text-xs text-text-secondary font-body mt-0.5">
                        {elevenVoices.length > 0
                          ? "Premium neural voices — click to preview."
                          : "US English only. Click a voice to preview."}
                      </p>
                    </div>
                    <div className="max-h-96 overflow-y-auto py-1 flex flex-col">
                      {elevenVoices.length > 0
                        ? elevenVoices.map((v) => {
                            const active = (savedVoiceURI || elevenDefaultId) === v.voice_id;
                            return (
                              <button
                                key={v.voice_id}
                                type="button"
                                data-testid={`voice-option-${v.name.toLowerCase()}`}
                                onClick={() => chooseVoice(v.voice_id)}
                                className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 font-body text-sm transition-colors flex-shrink-0 min-h-[56px] ${
                                  active ? "bg-primary/5 text-primary" : "text-text-primary hover:bg-slate-50"
                                }`}
                              >
                                <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                                  <span className="font-semibold leading-tight">{v.name}</span>
                                  <span className="text-xs text-text-secondary leading-tight">{v.description}</span>
                                </div>
                                {active && <Check className="w-4 h-4 shrink-0" />}
                              </button>
                            );
                          })
                        : availableVoices.slice(0, 6).map((v) => {
                            const active =
                              voiceRef.current?.voiceURI === v.voiceURI ||
                              savedVoiceURI === v.voiceURI;
                            return (
                              <button
                                key={v.voiceURI}
                                type="button"
                                data-testid={`voice-option-${v.name.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => chooseVoice(v.voiceURI)}
                                className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 font-body text-sm transition-colors ${
                                  active ? "bg-primary/5 text-primary" : "text-text-primary hover:bg-slate-50"
                                }`}
                              >
                                <span className="truncate">{v.name}</span>
                                {active && <Check className="w-4 h-4 shrink-0" />}
                              </button>
                            );
                          })}
                    </div>
                    <div className="border-t border-[#E2E8F0] px-4 py-2">
                      <p className="text-[11px] text-text-secondary font-body">
                        {elevenVoices.length > 0
                          ? "Powered by ElevenLabs — sounds identical on every device."
                          : "Voices vary by device. On iPhone/Mac, enable “Enhanced Voice” in Settings → Accessibility → Spoken Content for the clearest sound."}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {messages.length > 1 && (
                <Button
                  data-testid="print-button"
                  onClick={handlePrint}
                  variant="outline"
                  className="border-2 border-primary text-primary hover:bg-primary/10 font-body font-semibold"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Summary
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Chat Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <PHIDisclaimerBanner variant="chat" />
        </div>

        {/* Warm welcome hero — only visible before the user has replied.
            Fades away automatically once the conversation begins. */}
        {messages.length <= 1 && (
          <div
            data-testid="asher-welcome-hero"
            className="relative overflow-hidden bg-gradient-to-br from-[#EDF7F6] via-white to-[#FEF3EC] rounded-3xl border border-primary/20 shadow-sm mb-6 p-6 md:p-8"
          >
            {/* Decorative doorway glow */}
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true"></div>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-secondary/10 blur-3xl pointer-events-none" aria-hidden="true"></div>

            <div className="relative flex flex-col sm:flex-row items-center sm:items-start gap-6 md:gap-8">
              {/* Full-body Asher on the left */}
              <div className="flex-shrink-0 relative">
                <div className="absolute inset-0 rounded-full bg-primary/15 blur-2xl scale-90" aria-hidden="true"></div>
                <img
                  src="/asher-character-full.png?v=2"
                  alt="Asher, your care guide"
                  data-testid="asher-welcome-character"
                  onError={(e) => { e.currentTarget.src = "/asher-avatar.png"; }}
                  className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 object-contain drop-shadow-xl"
                />
              </div>

              {/* Warm greeting on the right */}
              <div className="flex-1 text-center sm:text-left">
                <p className="text-[11px] font-body font-bold uppercase tracking-[0.2em] text-primary mb-2">
                  Meet Asher
                </p>
                <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-accent leading-tight mb-3">
                  You don&apos;t have to walk this alone.
                </h2>
                <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-2">
                  We&apos;re here for you as you cross the threshold to a new part of life&apos;s journey.
                </p>
                <p className="font-body text-base md:text-lg text-text-primary leading-relaxed">
                  <span className="font-semibold">I&apos;m Asher</span> — your guide. I&apos;ll help you understand your options, sort through the care landscape, and figure out what feels right for your family. No pressure. Just a warm, honest conversation, on your terms.
                </p>
                <p className="font-body text-sm md:text-base text-text-secondary italic mt-4">
                  Whenever you&apos;re ready, tell me your first name below and we&apos;ll begin.
                </p>
                {elevenVoices.length > 0 && (
                  <button
                    type="button"
                    data-testid="prefer-to-read-btn"
                    onClick={toggleVoice}
                    className="mt-4 inline-flex items-center gap-2 text-xs font-body text-text-secondary hover:text-primary transition-colors"
                  >
                    {voiceEnabled ? (
                      <>
                        <VolumeX className="w-3.5 h-3.5" />
                        <span>Prefer to read? <span className="underline">Mute Asher&apos;s voice</span></span>
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-3.5 h-3.5" />
                        <span>Voice is muted. <span className="underline">Turn Asher&apos;s voice back on</span></span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] overflow-hidden mb-6">
          {/* Messages */}
          <div
            data-testid="chat-messages-container"
            className="h-[500px] overflow-y-auto p-6 space-y-4 chat-scroll"
          >
            {messages.map((msg, index) => (
              <div
                key={`${msg.timestamp}-${msg.role}-${index}`}
                data-testid={`message-${msg.role}-${index}`}
                className={`flex ${
                  msg.role === "user" ? "justify-end" : "justify-start"
                } chat-message-enter`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-6 py-4 ${
                    msg.role === "user"
                      ? "bg-primary text-white rounded-br-none"
                      : "bg-[#F1F5F9] text-text-primary rounded-bl-none border border-[#E2E8F0]"
                  }`}
                >
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-2 mb-2">
                      <img 
                        src="/asher-avatar.png?v=2" 
                        alt="Asher" 
                        onError={(e) => { e.currentTarget.src = "/asher-avatar-128.png"; }}
                        className="w-7 h-7 rounded-full object-cover ring-1 ring-primary/20"
                      />
                      <span className="text-xs font-body font-semibold text-text-secondary">Asher</span>
                    </div>
                  )}
                  <p className="text-base leading-relaxed font-body whitespace-pre-wrap">
                    {msg.content}
                  </p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-[#F1F5F9] rounded-2xl rounded-bl-none border border-[#E2E8F0] px-6 py-4 flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="text-base text-text-secondary font-body">Asher is thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-[#E2E8F0] p-4 bg-background">
            <div className="flex gap-3 items-center">
              <Input
                ref={inputRef}
                data-testid="chat-input"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isRecording ? "Listening…" : isTranscribing ? "Transcribing…" : "Type or tap the mic to talk"}
                disabled={isLoading || isRecording || isTranscribing}
                className="h-12 md:h-14 text-lg rounded-lg border-2 border-input focus:border-primary focus:ring-0 bg-white font-body"
              />
              {supportsMic && (
                <Button
                  data-testid="mic-button"
                  onClick={toggleRecording}
                  disabled={isLoading || isTranscribing}
                  title={isRecording ? "Stop and send" : "Talk to Asher"}
                  aria-label={isRecording ? "Stop recording" : "Start recording"}
                  className={`min-h-[48px] min-w-[48px] h-12 md:h-14 w-12 md:w-14 rounded-full font-bold shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 p-0 ${
                    isRecording
                      ? "bg-[#E07B54] text-white hover:bg-[#c8683e] animate-pulse"
                      : isTranscribing
                        ? "bg-slate-200 text-slate-500 cursor-wait"
                        : "bg-white border-2 border-primary text-primary hover:bg-primary/10"
                  }`}
                >
                  {isTranscribing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isRecording ? (
                    <Square className="w-5 h-5 fill-current" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </Button>
              )}
              <Button
                data-testid="send-button"
                onClick={handleSendMessage}
                disabled={isLoading || isRecording || isTranscribing || !inputMessage.trim()}
                className="min-h-[48px] min-w-[48px] h-12 md:h-14 px-6 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold shadow-md hover:shadow-lg transition-all duration-300 active:scale-95"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
            {(isRecording || isTranscribing) && (
              <p
                data-testid="mic-status"
                className="text-xs text-text-secondary font-body mt-2 ml-1"
              >
                {isRecording
                  ? "🎙️  Listening… tap the stop button when you're done."
                  : "✨  Transcribing your message…"}
              </p>
            )}
          </div>
        </div>

        {/* Narrow-Down Filter — three-stage inline component:
              1. Compact "Want to narrow?" prompt (default)
              2. Full panel if the family opts in
              3. Tiny "Add filters" link so they can reopen anytime
            Once Asher has established a care direction we keep this mounted;
            the internal stage controls what's visible so it never disappears
            for good — families can always come back to filters. */}
        {recommendedCareTypes && recommendedCareTypes.length > 0 && (
          <div className="mt-6" data-testid="asher-filter-card-section">
            <AsherFilterCard
              onSubmit={(f) => setNarrowFilters(f)}
              onSkip={() => setNarrowFilters(null)}
            />
          </div>
        )}

        {/* Provider Search */}
        {showProviderSearch && (
          <div ref={providerSearchRef} data-testid="chat-provider-search-section">
            <ProviderSearch
              careType={careTypeRecommendation}
              sessionId={sessionId}
              autoSearchLocation={autoSearchLocation}
              recommendedCareTypes={recommendedCareTypes}
              narrowFilters={narrowFilters}
            />
          </div>
        )}
      </main>
    </div>
  );
}
